import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { HeartHandshake, LogOut, Wallet, CalendarRange, FileText, ClipboardList, X, ChevronRight } from 'lucide-react';
import axios from 'axios';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';

const api = axios.create({ baseURL: '/api/aged-care-portal' });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('aged_portal_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
api.interceptors.response.use((r) => r, (err) => {
  if (err.response?.status === 401) {
    localStorage.removeItem('aged_portal_token');
    localStorage.removeItem('aged_portal_account');
    window.location.href = '/aged-care-portal/login';
  }
  return Promise.reject(err);
});

const money = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtD = (d) => { if (!d) return ''; try { return format(parseISO(String(d).slice(0, 10)), 'd MMM yyyy'); } catch { return String(d); } };
const fmtPeriod = (p) => { if (!p) return ''; try { return format(parseISO(`${p}-01`), 'MMMM yyyy'); } catch { return p; } };
const CAT_LABEL = { clinical: 'Clinical', independence: 'Independence', everyday: 'Everyday living' };
const CAT_TONE = { clinical: 'bg-teal-100 text-teal-700', independence: 'bg-sky-100 text-sky-700', everyday: 'bg-amber-100 text-amber-700' };
const STMT_TONE = { Draft: 'bg-slate-100 text-slate-600', Emailed: 'bg-emerald-100 text-emerald-700', Printed: 'bg-emerald-100 text-emerald-700' };

// Standalone Support at Home participant/family dashboard — read-only transparency: live budget,
// the current quarter's pool, monthly statements (with itemised services + contributions) and
// recent care-management activity. Mirrors the numbers the provider sees in the admin Funding tab.
export default function AgedCareDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    api.get('/overview').then((r) => setData(r.data)).catch((e) => {
      if (e.response?.status === 404) setData({ funding: null, quarter: null, statements: [], care_management: [] });
      else setData({ funding: null, quarter: null, statements: [], care_management: [] });
    });
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('aged_portal_token')) { navigate('/aged-care-portal/login', { replace: true }); return; }
    api.get('/me').then((r) => setMe(r.data)).catch(() => {});
    load();
  }, [navigate, load]);

  const openStatement = async (id) => {
    if (!id) return;
    try { const r = await api.get(`/statements/${id}`); setDetail(r.data); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not open the statement'); }
  };

  const logout = () => {
    localStorage.removeItem('aged_portal_token');
    localStorage.removeItem('aged_portal_account');
    navigate('/aged-care-portal/login', { replace: true });
  };

  const f = data?.funding;
  const q = data?.quarter;
  // A CHSP participant: no budget or statements, a contribution for each service instead.
  const chsp = data?.program === 'chsp' ? data.chsp : null;
  const usedPct = f && Number(f.budget) > 0 ? Math.min(100, Math.max(0, (Number(f.spent) / Number(f.budget)) * 100)) : 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow">
              <HeartHandshake size={18} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-slate-900 text-sm">{chsp ? 'Home support (CHSP)' : 'Support at Home'}</p>
              {me && <p className="text-xs text-slate-400">{me.client_name}{me.relationship ? ` · ${me.relationship}` : ''}</p>}
            </div>
          </div>
          <button onClick={logout} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-red-500 transition-colors"><LogOut size={15} /> Logout</button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* Welcome + budget headline */}
        <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-2xl p-5 text-white shadow-lg">
          <p className="text-sm opacity-80">Hi {me?.name || me?.first_name || 'there'}</p>
          <p className="text-xl font-bold mt-0.5">{me?.first_name ? `${me.first_name}'s` : 'Your'} {chsp ? 'home support services' : 'Support at Home budget'}</p>
          {f && (
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <span><span className="opacity-70">Budget</span> <strong>{money(f.budget)}</strong></span>
              <span><span className="opacity-70">Spent</span> <strong>{money(f.spent)}</strong></span>
              <span><span className="opacity-70">Remaining</span> <strong>{money(f.remaining)}</strong></span>
            </div>
          )}
        </div>

        {chsp ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-2 text-sm text-slate-700">
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><HeartHandshake size={15} className="text-teal-500" /> Commonwealth Home Support Programme</h2>
            <p>You receive services under CHSP{chsp.startDate ? ` from ${fmtD(chsp.startDate)}` : ''}{chsp.endDate ? ` to ${fmtD(chsp.endDate)}` : ''}. CHSP is funded by the Australian Government, so there is no budget to track and no monthly statement.</p>
            <p>{chsp.feeReductionPct >= 100 ? 'Your contribution has been waived.'
              : chsp.feeReductionPct > 0 ? `You pay a contribution for each service, reduced by ${chsp.feeReductionPct}%. Your provider invoices you for it.`
                : 'You pay a contribution for each service, which your provider invoices you for. Ask them if paying it would cause you hardship.'}</p>
          </div>
        ) : !data ? <p className="text-sm text-slate-400 py-10 text-center">Loading…</p> : !f ? (
          <div className="bg-white rounded-2xl border border-slate-200 py-14 text-center text-slate-400">
            <Wallet size={30} className="mx-auto opacity-30 mb-2" />
            <p className="text-sm">No Support at Home funding on file yet.</p>
            <p className="text-xs mt-1">Your provider will set this up — check back soon.</p>
          </div>
        ) : (
          <>
            {/* Budget bar */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <h2 className="text-sm font-bold text-slate-700 flex items-center gap-1.5 mb-3"><Wallet size={15} className="text-teal-500" /> Package budget</h2>
              <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div className={clsx('h-full rounded-full', Number(f.remaining) < 0 ? 'bg-rose-500' : usedPct > 85 ? 'bg-amber-400' : 'bg-teal-500')} style={{ width: `${usedPct}%` }} />
              </div>
              <div className="flex items-center justify-between text-xs mt-1.5">
                <span className="text-slate-400">{usedPct.toFixed(0)}% used</span>
                <span className={clsx('font-semibold tabular-nums', Number(f.remaining) < 0 ? 'text-rose-600' : 'text-emerald-600')}>{money(f.remaining)} left</span>
              </div>
            </div>

            {/* Current quarter pool */}
            {q && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h2 className="text-sm font-bold text-slate-700 flex items-center gap-1.5 mb-2"><CalendarRange size={15} className="text-teal-500" /> This quarter</h2>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
                  <span><span className="text-slate-400">Period</span> <strong>{fmtD(q.quarter_start)} – {fmtD(q.quarter_end)}</strong></span>
                  <span><span className="text-slate-400">Quarterly budget</span> <strong className="tabular-nums">{money(q.pool)}</strong></span>
                  {Number(q.carryover_in) > 0 && <span><span className="text-slate-400">Carried over</span> <strong className="tabular-nums">{money(q.carryover_in)}</strong></span>}
                </div>
              </div>
            )}

            {/* Statements */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 text-sm font-bold text-slate-700 flex items-center gap-1.5"><FileText size={15} className="text-teal-500" /> Monthly statements</div>
              {(data.statements || []).length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">No statements yet.</p> : (
                <div className="divide-y divide-slate-50">
                  {data.statements.map((s) => (
                    <button key={s.id} onClick={() => openStatement(s.id)} className="w-full px-5 py-3 flex items-center gap-3 text-left hover:bg-slate-50 transition group">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-700 group-hover:text-teal-700">{fmtPeriod(s.period)}</p>
                        <p className="text-[11px] text-slate-400">Closing balance {money(s.closing)} · your contribution {money(s.contributions)}</p>
                      </div>
                      <span className={clsx('text-[10px] font-bold px-2 py-1 rounded-full', STMT_TONE[s.status] || 'bg-slate-100 text-slate-600')}>{s.status || 'Draft'}</span>
                      <ChevronRight size={14} className="text-slate-300 group-hover:text-teal-400 shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Care management */}
            {(data.care_management || []).length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <h2 className="text-sm font-bold text-slate-700 flex items-center gap-1.5 mb-3"><ClipboardList size={15} className="text-teal-500" /> Recent care management</h2>
                <div className="space-y-2">
                  {data.care_management.map((c, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{c.activity_type || 'Care management contact'}</span>
                      <span className="text-xs text-slate-400">{fmtD(c.activity_date)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <p className="text-center text-[11px] text-slate-400 pb-6">Questions about anything here? Contact your provider — they can see the same numbers.</p>
      </div>

      {/* Statement detail */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 p-0 sm:p-4" onMouseDown={() => setDetail(null)}>
          <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[88vh] overflow-y-auto" onMouseDown={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-base font-bold text-slate-800">{fmtPeriod(detail.period)} statement</p>
                <p className="text-xs text-slate-400 mt-0.5">{detail.classification || 'Support at Home'}</p>
              </div>
              <button onClick={() => setDetail(null)} aria-label="Close" className="text-slate-400 hover:text-slate-600 shrink-0"><X size={20} /></button>
            </div>
            <div className="px-5 py-4">
              <div className="rounded-xl border border-slate-100 overflow-hidden mb-4">
                <div className="px-3 py-2 bg-slate-50 text-[10px] font-bold uppercase tracking-wide text-slate-400">Services delivered</div>
                {(detail.lines || []).length === 0 ? <p className="px-3 py-4 text-xs text-slate-400">No itemised services for this period.</p> : (
                  <div className="divide-y divide-slate-50">
                    {detail.lines.map((l, i) => (
                      <div key={i} className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-slate-700">{l.description || 'Service'}</p>
                            <p className="text-[10px] text-slate-400">{fmtD(l.service_date)}{Number(l.units) !== 1 ? ` · ${Number(l.units)} × ${money(l.rate)}` : ''}</p>
                          </div>
                          <span className={clsx('text-[9px] font-bold px-1.5 py-0.5 rounded-full', CAT_TONE[l.category] || 'bg-slate-100 text-slate-500')}>{CAT_LABEL[l.category] || l.category}</span>
                          <span className="text-xs font-semibold tabular-nums text-slate-700 shrink-0 w-16 text-right">{money(l.cost)}</span>
                        </div>
                        {Number(l.client_contribution) > 0 && (
                          <p className="text-[10px] text-slate-400 mt-0.5">Your contribution {money(l.client_contribution)} · government {money(l.gov_subsidy)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-1 text-sm">
                <Row label="Opening balance" v={detail.opening} />
                <Row label="+ Government subsidy" v={detail.subsidy} />
                {Number(detail.supplements) > 0 && <Row label="+ Supplements" v={detail.supplements} />}
                {Number(detail.fees) > 0 && <Row label={String(detail.scheme || '').toUpperCase() === 'HCP' ? '− Management fees' : '− Care management (10% of your budget)'} v={detail.fees} />}
                <Row label="− Services delivered (full price)" v={detail.expenditure} />
                <div className="flex items-center justify-between pt-2 mt-1 border-t-2 border-slate-200 font-bold text-slate-800">
                  <span>Closing balance</span><span className="tabular-nums">{money(detail.closing)}</span>
                </div>
                <p className="text-[11px] text-slate-500 pt-1">
                  Your contributions for these services come to {money(detail.contributions)}. They are part of the price above, and we invoice you for them separately.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ label, v }) => (
  <div className="flex items-center justify-between text-slate-600"><span>{label}</span><span className="tabular-nums">{money(v)}</span></div>
);
